import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { calendarApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import type { CalendarEvent } from "@/types";
import {
  Calendar,
  Clock,
  MapPin,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
} from "lucide-react";
import { getEventTypeConfig } from "@/utils/eventTypes";
import {
  parseApiDateAsBerlin,
  formatBerlinDate,
} from "@/utils/datetime";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, startOfWeek, endOfWeek } from "date-fns";
import { de } from "date-fns/locale";

// Re-export under the local name for the 11 call-sites below.
// Falls back to the current time when input is missing, matching the
// previous behaviour (callers use this in `start < end` comparisons
// where "now" is a reasonable sentinel). Returns a Date whose UTC
// fields are the wall-clock digits, so formatBerlinDate renders the
// same digits back to the user regardless of browser tz (see
// utils/datetime.ts for rationale).
function parseApiDate(dateStr: string | undefined | null): Date {
  return parseApiDateAsBerlin(dateStr) ?? new Date();
}

// Helper to check if event spans multiple days
function isMultiDayEvent(event: CalendarEvent): boolean {
  if (!event.end_date) return false;
  const start = parseApiDate(event.start_date);
  const end = parseApiDate(event.end_date);
  return !isSameDay(start, end);
}

// Chunk days into weeks
function chunkIntoWeeks(days: Date[]): Date[][] {
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

// Get multi-day events that overlap with a given week
function getMultiDayEventsForWeek(
  weekDays: Date[],
  events: CalendarEvent[],
  pendingProposals: CalendarEvent[]
): CalendarEvent[] {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  const allEvents = [...events, ...pendingProposals];
  return allEvents.filter(event => {
    if (!isMultiDayEvent(event)) return false;
    const eventStart = parseApiDate(event.start_date);
    const eventEnd = event.end_date ? parseApiDate(event.end_date) : eventStart;
    return eventStart <= weekEnd && eventEnd >= weekStart;
  });
}

// Compute grid column span for a multi-day event within a week
function getEventWeekSpan(event: CalendarEvent, weekDays: Date[]): { startCol: number; endCol: number } {
  const eventStart = parseApiDate(event.start_date);
  const eventEnd = event.end_date ? parseApiDate(event.end_date) : eventStart;
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  const effectiveStart = eventStart < weekStart ? weekStart : eventStart;
  const effectiveEnd = eventEnd > weekEnd ? weekEnd : eventEnd;
  const startCol = weekDays.findIndex(d => isSameDay(d, effectiveStart));
  const endCol = weekDays.findIndex(d => isSameDay(d, effectiveEnd));
  return { startCol: Math.max(0, startCol), endCol: Math.max(0, endCol) };
}

// Assign multi-day events to rows to avoid overlap
function assignMultiDayRows(
  events: CalendarEvent[],
  weekDays: Date[]
): CalendarEvent[][] {
  const rows: CalendarEvent[][] = [];
  for (const event of events) {
    const span = getEventWeekSpan(event, weekDays);
    let placed = false;
    for (const row of rows) {
      const overlaps = row.some(e => {
        const otherSpan = getEventWeekSpan(e, weekDays);
        return span.startCol <= otherSpan.endCol && span.endCol >= otherSpan.startCol;
      });
      if (!overlaps) {
        row.push(event);
        placed = true;
        break;
      }
    }
    if (!placed) rows.push([event]);
  }
  return rows;
}

export function CalendarPage() {
  const navigate = useNavigate();
  const { isAdmin: _isAdmin } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [pendingProposals, setPendingProposals] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [view, setView] = useState<"month" | "week" | "list">("month");

  useEffect(() => {
    loadEvents();
  }, [currentMonth]);

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await calendarApi.getByMonth(year, month);
      setEvents(response.data.data || []);
      setPendingProposals(response.data.pending_proposals || []);
    } catch (error) {
      console.error("Failed to load events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => 
      direction === "prev" ? subMonths(prev, 1) : addMonths(prev, 1)
    );
  };

  const navigateWeek = (direction: "prev" | "next") => {
    setCurrentMonth(prev => 
      direction === "prev" ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  const getEventsForDay = (day: Date) => {
    const allEvents = [...events, ...pendingProposals];
    return allEvents.filter(event => {
      const eventStart = parseApiDate(event.start_date);
      const eventEnd = event.end_date ? parseApiDate(event.end_date) : eventStart;
      return isSameDay(day, eventStart) || 
             (day >= eventStart && day <= eventEnd);
    });
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { locale: de });
  const calendarEnd = endOfWeek(monthEnd, { locale: de });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

  const selectedDateEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kalender</h1>
          <p className="text-sm text-gray-500 mt-1">
            {events.length} Termine in {format(currentMonth, "MMMM yyyy", { locale: de })}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setView("month")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                view === "month" 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monat
            </button>
            <button
              onClick={() => setView("week")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                view === "week" 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Woche
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                view === "list" 
                  ? "bg-white text-gray-900 shadow-sm" 
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Liste
            </button>
          </div>
          
          {/* New Event Button */}
          <Link
            to="/calendar/new"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neuer Termin
          </Link>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-4">
        <button
          onClick={() => view === "week" ? navigateWeek("prev") : navigateMonth("prev")}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        
        <h2 className="text-lg font-semibold text-gray-900">
          {view === "week" 
            ? `${format(startOfWeek(currentMonth, { locale: de }), "d.", { locale: de })} – ${format(endOfWeek(currentMonth, { locale: de }), "d. MMMM yyyy", { locale: de })}`
            : format(currentMonth, "MMMM yyyy", { locale: de })
          }
        </h2>
        
        <button
          onClick={() => view === "week" ? navigateWeek("next") : navigateMonth("next")}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : view === "month" ? (
        <>
          {/* Calendar Grid */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="py-2 text-center text-sm font-medium text-gray-500 border-r last:border-r-0 border-gray-200"
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days by Week */}
            {chunkIntoWeeks(calendarDays).map((weekDays, weekIdx) => {
              const weekMultiDayEvents = getMultiDayEventsForWeek(weekDays, events, pendingProposals);
              const multiDayRows = assignMultiDayRows(weekMultiDayEvents, weekDays);
              return (
                <div
                  key={weekIdx}
                  className="grid grid-cols-7"
                  style={multiDayRows.length > 0 ? { gridTemplateRows: `repeat(${multiDayRows.length}, 24px) auto` } : undefined}
                >
                  {/* Multi-day event bars — inside the same grid, spanning day columns */}
                  {multiDayRows.map((row, rIdx) =>
                    row.map((event) => {
                      const config = getEventTypeConfig(event.event_type);
                      const isPendingEvent = event.proposal_status === 'pending';
                      const isBirthday = !!event.contact_id;
                      const span = getEventWeekSpan(event, weekDays);
                      const handleClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (isBirthday && event.contact_id) {
                          navigate(`/contacts/${event.contact_id}`, { state: { fromCalendar: true, birthdayAge: event.birthday_age, birthdayDate: event.start_date } });
                        } else if (event.proposal_id) {
                          navigate('/proposals');
                        } else {
                          navigate(`/calendar/${event.id}`);
                        }
                      };
                      return (
                        <div
                          key={event.proposal_id ? `md-proposal-${event.proposal_id}` : (event.contact_id ? `md-birthday-${event.contact_id}` : `md-${event.id}`)}
                          onClick={handleClick}
                          style={{ gridColumn: `${span.startCol + 1} / ${span.endCol + 2}`, gridRow: rIdx + 1 }}
                          className={`z-10 text-xs px-1.5 py-0.5 mx-0.5 my-0.5 rounded truncate cursor-pointer flex items-center ${config.color} ${isPendingEvent ? 'opacity-60 border border-dashed border-current' : ''}`}
                          title={event.title}
                        >
                          {!event.is_all_day && event.end_date && (
                            <span className="text-[10px] tabular-nums flex-shrink-0 mr-1">
                              {formatBerlinDate(parseApiDate(event.start_date), "time")}–{formatBerlinDate(parseApiDate(event.end_date), "time")}
                            </span>
                          )}
                          <span className="truncate">{event.title}{isPendingEvent ? ' (ausstehend)' : ''}</span>
                        </div>
                      );
                    })
                  )}
                  {/* Day cells — single-day events only */}
                  {weekDays.map((day, idx) => {
                    const dayEvents = getEventsForDay(day).filter(e => !isMultiDayEvent(e));
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    const isToday = isSameDay(day, new Date());
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    return (
                      <div
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        style={{ gridColumn: idx + 1, gridRow: multiDayRows.length + 1 }}
                        className={`min-h-[100px] p-2 border-r border-b border-gray-200 last:border-r-0 cursor-pointer transition-colors ${
                          !isCurrentMonth ? "bg-gray-50" : ""
                        } ${isSelected ? "bg-primary-50" : "hover:bg-gray-50"}`}
                      >
                        <div className={`text-sm font-medium mb-1 ${
                          !isCurrentMonth ? "text-gray-400" :
                          isToday ? "text-primary-600" : "text-gray-700"
                        }`}>
                          {isToday && (
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-primary-600 text-white rounded-full text-xs">
                              {format(day, "d")}
                            </span>
                          )}
                          {!isToday && format(day, "d")}
                        </div>
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map((event) => {
                            const config = getEventTypeConfig(event.event_type);
                            const EventIcon = config.icon;
                            const isPendingEvent = event.proposal_status === 'pending';
                            const isBirthday = !!event.contact_id;
                            const handleClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (isBirthday && event.contact_id) {
                                navigate(`/contacts/${event.contact_id}`, { state: { fromCalendar: true, birthdayAge: event.birthday_age, birthdayDate: event.start_date } });
                              } else if (event.proposal_id) {
                                navigate('/proposals');
                              } else {
                                navigate(`/calendar/${event.id}`);
                              }
                            };
                            return (
                              <div
                                key={event.proposal_id ? `proposal-${event.proposal_id}` : (event.contact_id ? `birthday-${event.contact_id}` : event.id)}
                                onClick={handleClick}
                                className={`text-xs px-1.5 py-0.5 rounded truncate flex items-center gap-1 cursor-pointer ${config.color} ${isPendingEvent ? 'opacity-60 border border-dashed border-current' : ''}`}
                              >
                                <EventIcon className="h-3 w-3 flex-shrink-0" />
                                {!event.is_all_day && (
                                  <span className="text-[10px] tabular-nums flex-shrink-0">
                                    {formatBerlinDate(parseApiDate(event.start_date), "time")}
                                  </span>
                                )}
                                <span className="truncate">{event.title}{isPendingEvent ? ' (ausstehend)' : ''}</span>
                              </div>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <div className="text-xs text-gray-500 px-1">
                              +{dayEvents.length - 3} weitere
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          
          {/* Selected Date Events */}
          {selectedDate && selectedDateEvents.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Termine am {format(selectedDate, "EEEE, d. MMMM", { locale: de })}
              </h3>
              <div className="space-y-3">
                {selectedDateEvents.map((event) => (
                  <EventCard key={event.contact_id ? `birthday-${event.contact_id}` : event.id} event={event} isPending={event.proposal_status === 'pending'} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : view === "week" ? (
        <WeekView 
          currentWeekStart={startOfWeek(currentMonth, { locale: de })}
          events={events}
          pendingProposals={pendingProposals}
          navigate={navigate}
        />
      ) : (
        /* List View */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {events.length === 0 && pendingProposals.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Keine Termine in diesem Monat</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {[...events].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()).map((event) => (
                <EventCard key={event.contact_id ? `birthday-${event.contact_id}` : event.id} event={event} showDate />
              ))}
              {pendingProposals.map((event) => (
                <EventCard key={`proposal-${event.proposal_id}`} event={event} showDate isPending />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, showDate = false, isPending = false }: { event: CalendarEvent; showDate?: boolean; isPending?: boolean }) {
  const navigate = useNavigate();
  const config = getEventTypeConfig(event.event_type);
  const EventIcon = config.icon;
  const isBirthday = !!event.contact_id;
  
  const formatEventDate = (start: string, end?: string, isAllDay = false) => {
    const startDate = parseApiDate(start);
    const endDate = end ? parseApiDate(end) : null;

    if (isAllDay) {
      if (!endDate || isSameDay(startDate, endDate)) {
        return "Ganztägig";
      }
      return `${format(startDate, "dd.MM.", { locale: de })} - ${format(endDate, "dd.MM.", { locale: de })}`;
    }

    const timeStr = formatBerlinDate(startDate, "time");
    if (!endDate) return timeStr;

    const endTimeStr = formatBerlinDate(endDate, "time");
    return `${timeStr} - ${endTimeStr}`;
  };
  
  const handleCardClick = (e: React.MouseEvent) => {
    if (isBirthday && event.contact_id) {
      e.preventDefault();
      navigate(`/contacts/${event.contact_id}`, { state: { fromCalendar: true, birthdayAge: event.birthday_age, birthdayDate: event.start_date } });
    }
  };

  const birthdayLink = isBirthday && event.contact_id
    ? { pathname: `/contacts/${event.contact_id}`, state: { fromCalendar: true, birthdayAge: event.birthday_age, birthdayDate: event.start_date } }
    : undefined;

  return (
    <Link
      to={birthdayLink || (isPending ? `/proposals` : `/calendar/${event.id}`)}
      onClick={handleCardClick}
      className={`block p-4 hover:bg-gray-50 transition-colors ${isPending ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
          <EventIcon className="h-5 w-5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
              {config.label}
            </span>
            {isPending && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                Ausstehend
              </span>
            )}
            {showDate && (
              <span className="text-sm text-gray-500">
                {format(parseApiDate(event.start_date), "dd.MM.yyyy", { locale: de })}
              </span>
            )}
          </div>
          
          <h4 className="text-base font-medium text-gray-900 mt-1">{event.title}</h4>
          
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{formatEventDate(event.start_date, event.end_date, event.is_all_day)}</span>
            </div>
            
            {event.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                <span className="truncate max-w-[200px]">{event.location}</span>
              </div>
            )}
            
            {event.created_by_name && (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>{event.created_by_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function WeekView({
  currentWeekStart,
  events,
  pendingProposals,
  navigate
}: {
  currentWeekStart: Date;
  events: CalendarEvent[];
  pendingProposals: CalendarEvent[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const weekDaysFull = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: addDays(currentWeekStart, 6)
  });

  const hours = Array.from({ length: 17 }, (_, i) => i + 6);

  const getEventsForDay = (day: Date) => {
    const allEvents = [...events, ...pendingProposals];
    return allEvents.filter(event => {
      const eventStart = parseApiDate(event.start_date);
      const eventEnd = event.end_date ? parseApiDate(event.end_date) : eventStart;
      return isSameDay(day, eventStart) || (day >= eventStart && day <= eventEnd);
    });
  };

  const formatHour = (hour: number) => `${String(hour).padStart(2, "0")}:00`;

  const getEventTop = (event: CalendarEvent) => {
    if (event.is_all_day) return null;
    const startDate = parseApiDate(event.start_date);
    const hour = startDate.getHours();
    const minute = startDate.getMinutes();
    if (hour < 6 || hour > 22) return null;
    return (hour - 6) * 60 + minute;
  };

  const getEventHeight = (event: CalendarEvent) => {
    if (event.is_all_day || !event.end_date) return 30;
    const startDate = parseApiDate(event.start_date);
    const endDate = parseApiDate(event.end_date);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMin = Math.max(diffMs / 60000, 30);
    return diffMin;
  };

  const handleEventClick = (e: React.MouseEvent, event: CalendarEvent) => {
    e.stopPropagation();
    const isBirthday = !!event.contact_id;
    if (isBirthday && event.contact_id) {
      navigate(`/contacts/${event.contact_id}`, { state: { fromCalendar: true, birthdayAge: event.birthday_age, birthdayDate: event.start_date } });
    } else if (event.proposal_id) {
      navigate("/proposals");
    } else {
      navigate(`/calendar/${event.id}`);
    }
  };

  const allEvents = [...events, ...pendingProposals];
  const multiDayAllDay = allEvents.filter(e => {
    if (!e.is_all_day) return false;
    if (!e.end_date) return false;
    const s = parseApiDate(e.start_date);
    const en = parseApiDate(e.end_date);
    return !isSameDay(s, en);
  });
  const multiDayRows: CalendarEvent[][] = [];
  for (const event of multiDayAllDay) {
    const eventStart = parseApiDate(event.start_date);
    const eventEnd = parseApiDate(event.end_date!);
    const startCol = weekDays.findIndex(d => isSameDay(d, eventStart) || d > eventStart);
    const endCol = weekDays.findIndex(d => isSameDay(d, eventEnd));
    const effectiveStartCol = startCol < 0 ? 0 : startCol;
    const effectiveEndCol = endCol < 0 ? 6 : endCol;
    let placed = false;
    for (const row of multiDayRows) {
      const overlaps = row.some(e => {
        const es = parseApiDate(e.start_date);
        const ee = parseApiDate(e.end_date!);
        const sc = weekDays.findIndex(d => isSameDay(d, es) || d > es);
        const ec = weekDays.findIndex(d => isSameDay(d, ee));
        const esc = sc < 0 ? 0 : sc;
        const eec = ec < 0 ? 6 : ec;
        return effectiveStartCol <= eec && effectiveEndCol >= esc;
      });
      if (!overlaps) {
        row.push(event);
        placed = true;
        break;
      }
    }
    if (!placed) multiDayRows.push([event]);
  }
  const allDayRowCount = Math.max(1, multiDayRows.length);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-visible">
      <div
        className="grid"
        style={{
          gridTemplateColumns: "80px repeat(7, minmax(0, 1fr))",
        }}
      >
        <div className="sticky top-0 z-30 py-2 px-1 text-center text-sm font-medium text-gray-500 border-r border-gray-200 bg-white"></div>
        {weekDays.map((day, index) => {
          const isToday = isSameDay(day, new Date());
          return (
            <div
              key={day.toISOString()}
              className={`sticky top-0 z-30 py-2 px-1 text-center text-sm font-medium border-r last:border-r-0 border-gray-200 bg-white ${
                isToday ? "text-primary-600" : "text-gray-700"
              }`}
            >
              <div className="text-xs text-gray-500">{weekDaysFull[index]}</div>
              <div className={`text-lg font-semibold ${isToday ? "text-primary-600" : ""}`}>
                {isToday ? (
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-primary-600 text-white rounded-full">
                    {format(day, "d")}
                  </span>
                ) : (
                  format(day, "d")
                )}
              </div>
            </div>
          );
        })}

        <div className="sticky top-[52px] z-20 py-2 px-1 text-xs font-medium text-gray-500 border-r border-gray-200 border-t flex items-center justify-center bg-gray-50">
          Ganztägig
        </div>

        <div
          className="col-span-7 sticky top-[52px] z-20 border-t border-gray-200 bg-gray-50"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gridTemplateRows: `repeat(${allDayRowCount}, auto)`,
          }}
        >
          {multiDayRows.map((row, rIdx) =>
            row.map((event) => {
              const config = getEventTypeConfig(event.event_type);
              const isPendingEvent = event.proposal_status === "pending";
              const eventStart = parseApiDate(event.start_date);
              const eventEnd = parseApiDate(event.end_date!);
              const startCol = weekDays.findIndex(d => isSameDay(d, eventStart) || d > eventStart);
              const endCol = weekDays.findIndex(d => isSameDay(d, eventEnd));
              const effectiveStartCol = startCol < 0 ? 0 : startCol;
              const effectiveEndCol = endCol < 0 ? 6 : endCol;
              return (
                <div
                  key={event.proposal_id ? `mda-proposal-${event.proposal_id}` : (event.contact_id ? `mda-birthday-${event.contact_id}` : `mda-${event.id}`)}
                  onClick={(e) => handleEventClick(e, event)}
                  style={{
                    gridColumn: `${effectiveStartCol + 1} / ${effectiveEndCol + 2}`,
                    gridRow: rIdx + 1,
                  }}
                  className={`z-10 text-xs px-1.5 py-0.5 mx-0.5 my-0.5 rounded truncate cursor-pointer ${config.color} ${isPendingEvent ? "opacity-60 border border-dashed border-current" : ""}`}
                >
                  {event.title}{isPendingEvent ? " (ausstehend)" : ""}
                </div>
              );
            })
          )}
          {weekDays.map((day, idx) => {
            const dayEvents = getEventsForDay(day).filter(e => e.is_all_day && !isMultiDayEvent(e));
            return (
              <div
                key={`allday-col-${idx}`}
                style={{ gridColumn: idx + 1, gridRow: multiDayRows.length > 0 ? "span 1 / -1" : 1 }}
                className="p-1 border-r last:border-r-0 border-gray-200 space-y-0.5"
              >
                {dayEvents.map((event) => {
                  const config = getEventTypeConfig(event.event_type);
                  const isPendingEvent = event.proposal_status === "pending";
                  return (
                    <div
                      key={event.proposal_id ? `sda-proposal-${event.proposal_id}` : (event.contact_id ? `sda-birthday-${event.contact_id}` : `sda-${event.id}`)}
                      onClick={(e) => handleEventClick(e, event)}
                      className={`text-xs px-1.5 py-0.5 rounded truncate cursor-pointer ${config.color} ${isPendingEvent ? "opacity-60 border border-dashed border-current" : ""}`}
                    >
                      {event.title}{isPendingEvent ? " (ausstehend)" : ""}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {hours.map((hour) => (
          <>
            <div
              key={`hour-label-${hour}`}
              className="py-1 px-1 text-xs text-gray-400 border-r border-gray-200 border-t flex items-start justify-center pt-1"
            >
              {formatHour(hour)}
            </div>
            {weekDays.map((day) => {
              const dayEvents = getEventsForDay(day).filter(e => {
                if (e.is_all_day) return false;
                if (isMultiDayEvent(e)) return false;
                const eventStart = parseApiDate(e.start_date);
                return eventStart.getHours() === hour ||
                  (eventStart.getHours() < hour && (!e.end_date || parseApiDate(e.end_date).getHours() >= hour));
              });
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={`${day.toISOString()}-${hour}`}
                  className={`relative p-0.5 border-r last:border-r-0 border-gray-200 border-t min-h-[60px] ${isToday ? "bg-primary-50/30" : ""}`}
                >
                  {dayEvents.map((event) => {
                    const config = getEventTypeConfig(event.event_type);
                    const isPendingEvent = event.proposal_status === "pending";
                    const top = getEventTop(event);
                    const height = getEventHeight(event);
                    const style = top !== null
                      ? { position: "absolute" as const, top: `${top % 60}px`, height: `${Math.min(height, 55)}px`, left: "2px", right: "2px", zIndex: 10 }
                      : {};
                    return (
                      <div
                        key={event.proposal_id ? `proposal-${event.proposal_id}` : (event.contact_id ? `birthday-${event.contact_id}` : event.id)}
                        onClick={(e) => handleEventClick(e, event)}
                        style={style}
                        className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer overflow-hidden ${config.color} ${isPendingEvent ? "opacity-60 border border-dashed border-current" : ""}`}
                      >
                        {event.title}{isPendingEvent ? " (ausstehend)" : ""}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}
